// Free listing refresh — scrape the property's URL and persist what we learn
// (title, photo gallery, description, address, price, pin) + lazily enrich
// (homes estimate, suburb rents, OSM amenities). Pure client orchestration
// over the Cloud Run endpoints; the only Firestore write is one
// rules-gated updateProperty on the canonical doc (S4).
import { scrapeProperty, geocode, enrichProperty } from './api.js';
import { updateProperty } from './firestore.js';
import { auth } from './firebase.js';

/** Returns the patch applied (or null if the property has no URL).
 * Guests (anonymous sessions) can't use backend /api/* features — scrape/
 * geocode/enrich are login-only (server 403). Return null so callers treat
 * it as "nothing to do", not as a failure. */
export async function refreshListing(pid, sourceUrl, currentTitle) {
  if (auth.currentUser?.isAnonymous) return null;
  if (!sourceUrl) return null;
  const meta = await scrapeProperty(pid);
  const patch = { metadata: { scraped_at: Date.now() } };
  // The name defaults to the address once the scrape knows it — URL-created
  // properties start as the bare host, which is a poor name. Only fills the
  // title while it's still that unedited placeholder; a renamed property
  // keeps its name (the address stays visible in the address row / map).
  const hostname = sourceUrl ? new URL(sourceUrl).hostname : null;
  const isDefaultTitle = !currentTitle || (hostname && currentTitle === hostname);
  if (isDefaultTitle) {
    if (meta.address) patch.title = meta.address;
    else if (meta.title) patch.title = meta.title;
  }
  if (meta.image_url) patch.metadata.image_url = meta.image_url;
  if (Array.isArray(meta.image_urls) && meta.image_urls.length) {
    patch.metadata.images = meta.image_urls;
  }
  if (meta.description) patch.metadata.description = meta.description;
  if (meta.price) patch.metadata.price = meta.price;
  if (meta.listed_date) patch.metadata.listed_date = meta.listed_date;
  if (meta.address) {
    patch.metadata.address = meta.address;
    patch.address = meta.address;
  }
  // The listing's own JSON-LD geo is the exact pin — prefer it over geocoding.
  if (meta.geo && typeof meta.geo.lat === 'number' && typeof meta.geo.lng === 'number') {
    patch.lat = meta.geo.lat;
    patch.lng = meta.geo.lng;
  } else if (meta.address) {
    try {
      const { lat, lng } = await geocode(meta.address);
      if (typeof lat === 'number' && typeof lng === 'number') {
        patch.lat = lat;
        patch.lng = lng;
      }
    } catch (e) {
      console.warn('geocode failed', e);
    }
  }
  await updateProperty(pid, patch);
  return patch;
}

/**
 * Lazy free enrichment — runs once per property (guarded by enrich.enriched_at)
 * and folds the result into the property doc. Re-runs once when a property was
 * enriched before the schools source shipped (enriched_at set but no schools
 * field and coords now present). Callers shouldn't await it.
 */
export async function enrichOnce(pid, prop) {
  if (auth.currentUser?.isAnonymous) return null;
  if (!prop?.source_url) return null;
  const has = prop.enrich?.enriched_at;
  // Re-enrich once when a property was enriched before the schools source
  // shipped (enriched_at set but no schools field) and coords now exist.
  const needsSchools =
    !has || (prop.enrich?.schools === undefined && typeof prop.lat === 'number');
  if (!needsSchools) return null;
  try {
    const enrich = await enrichProperty(pid);
    await updateProperty(pid, { enrich: { ...enrich, enriched_at: Date.now() } });
    return enrich;
  } catch (e) {
    console.warn('enrich failed', e);
    return null;
  }
}
